// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

// Define the type for Google Analytics handler configuration
export type xGoogleAnalyticsHandlerType = {
    trackGoogleAnalytics?: boolean;
    // Basic Events
    checkoutFlowEvents?: boolean;
    addToCartEvent?: boolean;
    addToCartEventName?: string;
    purchaseEvent?: boolean;
    purchaseEventName?: string;
    removeFromCartLineEvent?: boolean;
    removeFromCartLineEventName?: string;
    pdpViewEvent?: boolean;
    pdpViewEventName?: string;
    productImpressionEvent?: boolean;
    productImpressionEventName?: string;
    selectItemEvent?: boolean;
    selectItemEventName?: string;
    selectPromotionEvent?: boolean;
    selectPromotionEventName?: string;
    searchEvent?: boolean;
    searchEventName?: string;
    noStockSearchEvent?: boolean;
    noStockSearchEventName?: string;
    viewCartEvent?: boolean;
    viewCartEventName?: string;
    // Settings
    includeProductBrand?: boolean;
    includeProductCategory?: boolean;
    // Flags
    shouldTrackPayment?: boolean;
    shouldTrackBeginOnePageCheckout?: boolean;
    shouldTrackNoStockSearch?: boolean;
    affiliation?: string;
    // Custom Events
    loginEvent?: boolean;
    loginEventName?: string;
    setDasboardAsHomepageEvent?: boolean;
    setDasboardAsHomepageEventName?: string;
    returnRequestEvent?: boolean;
    returnRequestEventName?: string;
    viewWishlistEvent?: boolean;
    viewWishlistEventName?: string;
    trackQuickLinks?: boolean;
    trackQuickLinksEventName?: string;
    socialButtonsClickEvent?: boolean;
    socialButtonsClickEventName?: string;
    pdpProductResourcesClickEvent?: boolean;
    pdpProductResourcesClickEventName?: string;
    pdpPrintEvent?: boolean;
    pdpPrintEventName?: string;
    compareProductsEvent?: boolean;
    compareProductsEventName?: string;
    trainingFilterEvent?: boolean;
    trainingFilterEventName?: string;
    trainingSearchEventName?: string;
    pdpResourceDownloadEvent?: boolean;
    pdpResourceDownloadEventName?: string;
    quickOrderEvent?: boolean;
    quickOrderEventName?: string;
    toolBarLinkEvent?: boolean;
    toolBarLinkEventName?: string;
    navigationBarLinkEvent?: boolean;
    navigationBarLinkEventName?: string;
};

// Define the state type for Google Analytics
export type xGoogleAnalyticsStateType = Readonly<xGoogleAnalyticsHandlerType>;

// Initialize the default state for Google Analytics
const initialXGoogleAnalytics: xGoogleAnalyticsStateType = {
    trackGoogleAnalytics: false,
    // Basic Events
    checkoutFlowEvents: false,
    addToCartEvent: false,
    addToCartEventName: "add_to_cart",
    purchaseEvent: false,
    purchaseEventName: "purchase",
    removeFromCartLineEvent: false,
    removeFromCartLineEventName: "remove_from_cart",
    pdpViewEvent: false,
    pdpViewEventName: "view_item",
    productImpressionEvent: false,
    productImpressionEventName: "view_item_list",
    selectItemEvent: false,
    selectItemEventName: "select_item",
    selectPromotionEvent: false,
    selectPromotionEventName: "",
    searchEvent: false,
    searchEventName: "search",
    noStockSearchEvent: false,
    noStockSearchEventName: "nostock_search",
    viewCartEvent: false,
    viewCartEventName: "view_cart",
    // Settings
    includeProductBrand: false,
    includeProductCategory: false,
    // Flags
    shouldTrackPayment: false,
    shouldTrackBeginOnePageCheckout: false,
    shouldTrackNoStockSearch: false,
    affiliation: "",
    // Custom Events
    loginEvent: false,
    loginEventName: "login",
    setDasboardAsHomepageEvent: false,
    setDasboardAsHomepageEventName: "make_dash_hmpg",
    returnRequestEvent: false,
    returnRequestEventName: "return_request",
    viewWishlistEvent: false,
    viewWishlistEventName: "view_product_list",
    trackQuickLinks: false,
    trackQuickLinksEventName: "quick_links",
    socialButtonsClickEvent: false,
    socialButtonsClickEventName: "social_button_click",
    pdpProductResourcesClickEvent: false,
    pdpProductResourcesClickEventName: "pdp_prod_resources",
    pdpPrintEvent: false,
    pdpPrintEventName: "pdp_print",
    compareProductsEvent: false,
    compareProductsEventName: "compare_products",
    trainingFilterEvent: false,
    trainingFilterEventName: "training_filtering",
    trainingSearchEventName: "training_search",
    pdpResourceDownloadEvent: false,
    pdpResourceDownloadEventName: "resource_download",
    quickOrderEvent: false,
    quickOrderEventName: "quick_order",
    toolBarLinkEvent: false,
    toolBarLinkEventName: "tool_bar",
    navigationBarLinkEvent: false,
    navigationBarLinkEventName: "nav_bar",
} as xGoogleAnalyticsStateType;

// Define the reducer to handle Google Analytics actions
const reducer = {
    // Handle updating Google Analytics configuration
    "xGoogleAnalytics/update": (draft: Draft<xGoogleAnalyticsStateType>, action: { trackGoogleAnalytics: boolean, data: xGoogleAnalyticsHandlerType }) => {
        draft.trackGoogleAnalytics = action.trackGoogleAnalytics;
        // Basic Events
        draft.checkoutFlowEvents = action.data.checkoutFlowEvents ?? false;
        draft.addToCartEvent = action.data.addToCartEvent ?? false;
        draft.addToCartEventName = action.data.addToCartEventName ?? "add_to_cart";
        draft.purchaseEvent = action.data.purchaseEvent ?? false;
        draft.purchaseEventName = action.data.purchaseEventName ?? "purchase";
        draft.removeFromCartLineEvent = action.data.removeFromCartLineEvent ?? false;
        draft.removeFromCartLineEventName = action.data.removeFromCartLineEventName ?? "remove_from_cart";
        draft.pdpViewEvent = action.data.pdpViewEvent ?? false;
        draft.pdpViewEventName = action.data.pdpViewEventName ?? "view_item";
        draft.productImpressionEvent = action.data.productImpressionEvent ?? false;
        draft.productImpressionEventName = action.data.productImpressionEventName ?? "view_item_list";
        draft.selectItemEvent = action.data.selectItemEvent ?? false;
        draft.selectItemEventName = action.data.selectItemEventName ?? "select_item";
        draft.selectPromotionEvent = action.data.selectPromotionEvent ?? false;
        draft.selectPromotionEventName = action.data.selectPromotionEventName ?? "selectPromotionEventName";
        draft.searchEvent = action.data.searchEvent ?? false;
        draft.searchEventName = action.data.searchEventName ?? "search";
        draft.noStockSearchEvent = action.data.noStockSearchEvent ?? false;
        draft.noStockSearchEventName = action.data.noStockSearchEventName ?? "nostock_search";
        draft.viewCartEvent = action.data.viewCartEvent ?? false;
        draft.viewCartEventName = action.data.viewCartEventName ?? "view_cart";
        // Settings
        draft.includeProductBrand = action.data.includeProductBrand ?? false;
        draft.includeProductCategory = action.data.includeProductCategory ?? false;
        // Custom Events
        draft.loginEvent = action.data.loginEvent ?? false;
        draft.loginEventName = action.data.loginEventName ?? "login";
        draft.setDasboardAsHomepageEvent = action.data.setDasboardAsHomepageEvent ?? false;
        draft.setDasboardAsHomepageEventName = action.data.setDasboardAsHomepageEventName ?? "make_dash_hmpg";
        draft.returnRequestEvent = action.data.returnRequestEvent ?? false;
        draft.returnRequestEventName = action.data.returnRequestEventName ?? "return_request";
        draft.viewWishlistEvent = action.data.viewWishlistEvent ?? false;
        draft.viewWishlistEventName = action.data.viewWishlistEventName ?? "view_product_list";
        draft.trackQuickLinks = action.data.trackQuickLinks ?? false;
        draft.trackQuickLinksEventName = action.data.trackQuickLinksEventName ?? "quick_links";
        draft.socialButtonsClickEvent = action.data.socialButtonsClickEvent ?? false;
        draft.socialButtonsClickEventName = action.data.socialButtonsClickEventName ?? "social_button_click";
        draft.pdpProductResourcesClickEvent = action.data.pdpProductResourcesClickEvent ?? false;
        draft.pdpProductResourcesClickEventName = action.data.pdpProductResourcesClickEventName ?? "pdp_prod_resources";
        draft.pdpPrintEvent = action.data.pdpPrintEvent ?? false;
        draft.pdpPrintEventName = action.data.pdpPrintEventName ?? "pdp_print";
        draft.compareProductsEvent = action.data.compareProductsEvent ?? false;
        draft.compareProductsEventName = action.data.compareProductsEventName ?? "compare_products";
        draft.trainingFilterEvent = action.data.trainingFilterEvent ?? false;
        draft.trainingFilterEventName = action.data.trainingFilterEventName ?? "training_filtering";
        draft.trainingSearchEventName = action.data.trainingSearchEventName ?? "training_search";
        draft.pdpResourceDownloadEvent = action.data.pdpResourceDownloadEvent ?? false;
        draft.pdpResourceDownloadEventName = action.data.pdpResourceDownloadEventName ?? "resource_download";
        draft.quickOrderEvent = action.data.quickOrderEvent ?? false;
        draft.quickOrderEventName = action.data.quickOrderEventName ?? "quick_order";
        draft.toolBarLinkEvent = action.data.toolBarLinkEvent ?? false;
        draft.toolBarLinkEventName = action.data.toolBarLinkEventName ?? "tool_bar";
        draft.navigationBarLinkEvent = action.data.navigationBarLinkEvent ?? false;
        draft.navigationBarLinkEventName = action.data.navigationBarLinkEventName ?? "nav_bar";
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackPayment": (draft: Draft<xGoogleAnalyticsStateType>, action: { shouldTrackPayment: boolean }) => {
        draft.shouldTrackPayment = action.shouldTrackPayment;
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout": (draft: Draft<xGoogleAnalyticsStateType>, action: { shouldTrackBeginOnePageCheckout: boolean }) => {
        draft.shouldTrackBeginOnePageCheckout = action.shouldTrackBeginOnePageCheckout;
    },
    // Handle setting the nostock search tracking flag
    "xGoogleAnalytics/setShouldTrackNoStockSearch": (draft: Draft<xGoogleAnalyticsStateType>, action: { shouldTrackNoStockSearch: boolean }) => {
        draft.shouldTrackNoStockSearch = action.shouldTrackNoStockSearch;
    },
    // Handle setting the affiliation
    "xGoogleAnalytics/setAffiliation": (draft: Draft<xGoogleAnalyticsStateType>, action: { affiliation: string }) => {
        draft.affiliation = action.affiliation;
    },
};

// Create the Google Analytics reducer using Immer for immutability
const XGoogleAnalytics = createTypedReducerWithImmer(initialXGoogleAnalytics, reducer);

// Add the Google Analytics reducer to the application's reducers
(reducers as any).xGoogleAnalytics = XGoogleAnalytics;

// Define the application state including Google Analytics state
export type xGoogleAnalyticsState = {
    xGoogleAnalytics: xGoogleAnalyticsStateType;
} & ApplicationState;

// Define the type for Google Analytics reducers
type xGoogleAnalyticsReducers = {
    [key: string]: typeof XGoogleAnalytics;
};

// Define the type for Google Analytics actions
export type xGoogleAnalyticsActions = Parameters<xGoogleAnalyticsReducers[keyof xGoogleAnalyticsReducers]>[1] | AnyAction;
